/**
 * @voltxampere/web — Wire color gradient overlay (Sprint 30-4)
 *
 * Paints each wire's stroke in its net's voltage color. Drawn on the
 * fx canvas BETWEEN the heatmap (Sprint 30-3, behind) and the meteor
 * particles (Sprint 29-5, on top), so the user reads:
 *
 *   1. Halo halkaları (where node potentials live, Sprint 30-3)
 *   2. Wire stroke renkleri (which net carries which potential, S30-4)
 *   3. Brass-hot meteors (where current flows, Sprint 29)
 *
 * Per-net voltage is constant by KCL — schema's `wire.netId` reduces
 * any wire to a single net, so wire stroke is uniform along the path.
 * The "start V → end V gradient" framing in PHASE_2_LIVE_STUDIO §4.3
 * collapses to a degenerate same-color gradient, which we still emit
 * via `createLinearGradient` so a future schema extension (multi-net
 * wire splices) can plug into the same draw call without rewriting.
 *
 * HSL space for color interpolation (Şefim 2026-05-02 notu): RGB lerp
 * across the vfLow→vfMid→vfHigh palette can produce muddy intermediate
 * colors (e.g. blue + brass mid-point greys). HSL preserves the brand
 * tonality across the gradient — the saturation + lightness lerp gives
 * smoother visual transitions when the heatmap halo bleeds against the
 * stroke beneath it.
 *
 * The renderer reads `currentVoltageSamples` (already filtered + ground-
 * excluded by fx-state.ts). Wires whose net has no sample (e.g. ground
 * net wires) skip the colored stroke and stay invisible on this layer
 * (the schematic canvas behind still paints them in the default ivory).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Wire } from "@voltxampere/schema";
import { voltageRange, voltageToColor, type NodeVoltageSample, type RGBA } from "@voltxampere/fx";

/** Stroke width in world units. Mockup §E uses 1.5px nominal at zoom 1. */
const STROKE_WIDTH_BASE = 1.5;
/** Stroke alpha — independent of glow (animation) so it persists in DC mode. */
const STROKE_ALPHA = 0.55;

export interface WireGradientDrawOpts {
  readonly viewport: {
    readonly center: { x: number; y: number };
    readonly zoom: number;
  };
  readonly cssWidth: number;
  readonly cssHeight: number;
  readonly dpr: number;
  /** All wires from the active circuit. */
  readonly wires: readonly Wire[];
  /** Voltage samples — same source as the heatmap layer. */
  readonly samples: readonly NodeVoltageSample[];
}

/**
 * Paint colored wire strokes onto the overlay canvas.
 *
 * Caller is responsible for clearing the canvas. This helper sets up
 * its own world transform, paints with `globalCompositeOperation` =
 * "screen" so it composes with the heatmap halos beneath, and resets
 * to source-over before returning.
 *
 * Empty samples → no-op. Wires with no matching sample (ground nets)
 * are skipped — the schematic canvas paints them in default ivory.
 */
export function drawWireGradient(ctx: CanvasRenderingContext2D, opts: WireGradientDrawOpts): void {
  if (opts.samples.length === 0 || opts.wires.length === 0) return;

  // netId → voltage lookup (samples already filtered ground out)
  const voltageByNet = new Map<string, number>();
  for (const sample of opts.samples) {
    voltageByNet.set(sample.netId, sample.voltage);
  }

  const range = voltageRange(opts.samples);

  ctx.save();
  ctx.setTransform(opts.dpr, 0, 0, opts.dpr, 0, 0);
  // World transform — mirrors the schematic + heatmap layers.
  const z = opts.viewport.zoom;
  ctx.translate(opts.cssWidth / 2, opts.cssHeight / 2);
  ctx.scale(z, z);
  ctx.translate(-opts.viewport.center.x, -opts.viewport.center.y);

  // Screen blend stays consistent with the heatmap below — colored
  // strokes brighten when they overlap halo cores rather than going
  // muddy from over-painting.
  ctx.globalCompositeOperation = "screen";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  // Stroke width is in world units; divide by zoom keeps visual width
  // constant on screen across pan/zoom (matches Sprint 29 particle
  // radius scaling).
  ctx.lineWidth = STROKE_WIDTH_BASE / z;

  for (const wire of opts.wires) {
    const voltage = voltageByNet.get(wire.netId);
    if (voltage === undefined) continue;
    if (wire.path.length < 2) continue;
    const color = voltageToColor(voltage, range);
    const blendedColor = lerpColorsHSL(color, color, 0); // degenerate same-color
    ctx.strokeStyle = rgbaString(blendedColor, STROKE_ALPHA);
    ctx.beginPath();
    const first = wire.path[0];
    if (!first) continue;
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < wire.path.length; i++) {
      const p = wire.path[i];
      if (!p) continue;
      ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
  }

  ctx.globalCompositeOperation = "source-over";
  ctx.restore();
}

/**
 * Linearly interpolate two RGBA colors via HSL space.
 *
 * Sprint 30 use case is degenerate (start === end) but the HSL helper
 * is exposed for future multi-net wire splice support. HSL avoids the
 * RGB-lerp greying that happens when complementary colors meet at the
 * midpoint — the brand palette's vfLow (blue) ↔ vfHigh (brass) pair
 * would muddy in RGB but stays warm-saturated in HSL.
 */
function lerpColorsHSL(a: RGBA, b: RGBA, t: number): RGBA {
  if (t <= 0) return a;
  if (t >= 1) return b;
  const aHsl = rgbToHsl(a);
  const bHsl = rgbToHsl(b);
  const h = lerpHue(aHsl.h, bHsl.h, t);
  const s = aHsl.s + (bHsl.s - aHsl.s) * t;
  const l = aHsl.l + (bHsl.l - aHsl.l) * t;
  const rgb = hslToRgb({ h, s, l });
  return { r: rgb.r, g: rgb.g, b: rgb.b, a: a.a + (b.a - a.a) * t };
}

interface HSL {
  /** Hue in [0, 360) degrees. */
  readonly h: number;
  /** Saturation in [0, 1]. */
  readonly s: number;
  /** Lightness in [0, 1]. */
  readonly l: number;
}

function rgbToHsl(rgb: RGBA): HSL {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;
  return { h, s, l };
}

function hslToRgb(hsl: HSL): { r: number; g: number; b: number } {
  if (hsl.s === 0) {
    const v = Math.round(hsl.l * 255);
    return { r: v, g: v, b: v };
  }
  const q = hsl.l < 0.5 ? hsl.l * (1 + hsl.s) : hsl.l + hsl.s - hsl.l * hsl.s;
  const p = 2 * hsl.l - q;
  const h = hsl.h / 360;
  return {
    r: Math.round(hueToRgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hueToRgb(p, q, h) * 255),
    b: Math.round(hueToRgb(p, q, h - 1 / 3) * 255),
  };
}

function hueToRgb(p: number, q: number, t: number): number {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

/**
 * Lerp two hues taking the shorter circular path so blue (240°) →
 * brass (35°) goes through the warm side (red 0°) rather than the
 * cold side (green 120°).
 */
function lerpHue(a: number, b: number, t: number): number {
  let d = b - a;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return (a + d * t + 360) % 360;
}

function rgbaString(color: RGBA, alpha: number): string {
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${String(color.r)},${String(color.g)},${String(color.b)},${String(a)})`;
}
